import { styled } from "@material-ui/core";
import { useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { some } from "lodash";
import { Flex, Loading, PrimaryButton, div, span } from "lqx-react/lib";
import React from "react";
import { Can } from "src/app/services/can";
import { AssetEventLatest, AssetEventProcessStatus, GenericExternalSystemType } from "../api/assetEvent";
import { ExternalSystemRequest } from "../api/externalSystemRequest";
import { InblockedAssetEventStatusItemList } from "../ingest/inblocked-asset-event-status-item-list";
import { poll } from "../shared";
import { RemittanceAdviceExternalSystems } from "../ingest/remittance-advices/externalSystems";
import { PlatformProducts } from "../api/platformProducts";
import { getPlatformCode } from "../api/endpoint";

export const RemittanceAdviceSendToTab = ({ assetId, assetUid }) => {
    const externalSystemRequest = useModel(ExternalSystemRequest);
    const assetEventsLatest = useCollection.of(AssetEventLatest);
    const pollAssetEventsLatest = useCollection.of(AssetEventLatest);
    const platformProducts = useModel(PlatformProducts);

    const isReady = useIO(async () => {
        await assetEventsLatest.fetch({ params: { assetId: assetId } });
        await platformProducts.getPlatformProducts(getPlatformCode()).catch(e => console.error(e));

        populateDefaultEvents();
        startPollingAfterReloadPage();
    }, [])

    const startPollingAfterReloadPage = () => {
        let processingEvents = assetEventsLatest.filter(x => x.assetEventStatus === AssetEventProcessStatus.processing);
        if (processingEvents.length) {
            pollAssetEvents();
        }
    }

    const populateDefaultEvents = () => {
        let allAssetEvents = [{ externalSystemType: GenericExternalSystemType.scf },
        { externalSystemType: GenericExternalSystemType.inblock },
        { externalSystemType: GenericExternalSystemType.abf }];
        if (assetEventsLatest.length < 3) {
            if (assetEventsLatest.length == 0) {
                assetEventsLatest.set(allAssetEvents);
            } else {
                let difEvents = allAssetEvents.filter(e => !some(assetEventsLatest.models, { externalSystemType: e.externalSystemType }));
                assetEventsLatest.add(difEvents);
            }
        }
    }

    const sendToExternalSystem = async (latestAssetEvent, externalSystem) => {
        if (!assetUid) return;

        if (latestAssetEvent) {
            latestAssetEvent.assetEventStatus = AssetEventProcessStatus.processing;
        }

        externalSystemRequest.set({
            to: externalSystem,
            assetType: "Remittance Advice",
            assetUids: [assetUid],
        });
        await externalSystemRequest.save();

        pollAssetEvents();
    };

    const shouldPoll = () => {
        var result = pollAssetEventsLatest.length == 0 || !pollAssetEventsLatest.every(x => x.assetEventStatus == 2);
        assetEventsLatest.assignFrom(pollAssetEventsLatest);
        return result;
    }

    const pollAssetEvents = async () => {
        const shouldContinuePolling = () => shouldPoll();

        setTimeout(async () => await poll(async () => await pollAssetEventsLatest.fetch({ params: { assetId: assetId } }), shouldContinuePolling, 5000), 5000);
    };

    const $width = useLinked(450);

    return (isReady ?
        <Container>
            {
                RemittanceAdviceExternalSystems
                    .filter(es => {
                        const hasPermission = !es.requiredPermission || Can(es.requiredPermission);
                        if (es.externalSystemType === GenericExternalSystemType.pts) {
                            return hasPermission && platformProducts.items.models.some((item) => item.code === 'PTS' && item.sendToProductEnabled === true);;
                        }
                        if (es.externalSystemType === GenericExternalSystemType.inblock) {
                            return hasPermission && platformProducts.items.models.some((item) => item.code === 'INMATCH');;
                        }
                        return hasPermission;
                    })
                    .map(externalSystem => {
                        const latestAssetEvent = assetEventsLatest.filter(x => x.externalSystemType === externalSystem.externalSystemType)[0];
                        return (
                            <TileFlex>
                                <Flex row>
                                    <InfoFlex column>
                                        <HeaderField>{externalSystem.title}</HeaderField>
                                        <FooterField>
                                            {latestAssetEvent && latestAssetEvent.event}
                                        </FooterField>
                                    </InfoFlex>
                                    <IconMessageFlex>
                                        <InblockedAssetEventStatusItemList assetEvent={latestAssetEvent} externalSystem={externalSystem}></InblockedAssetEventStatusItemList>
                                    </IconMessageFlex>
                                    <ActionFlex column>
                                        <ActionButton onClick={() => sendToExternalSystem(latestAssetEvent, externalSystem.type)} color={latestAssetEvent && latestAssetEvent.assetUpdated ? "orange" : "green"}>
                                            {latestAssetEvent && latestAssetEvent.assetUpdated ? "Send Update" : "Send"}
                                        </ActionButton>
                                    </ActionFlex>
                                </Flex>
                            </TileFlex>
                        );
                    })
            }
        </Container> : <Loading />)
}

const Container = div({
    padding: '15px'
});

const ActionButton = styled(PrimaryButton)({
    minWidth: '105px',
    transition: 'transform 0.5s',
    '&:hover': {
        transform: 'scale(1.05)'
    }
})

const TileFlex = div({
    backgroundImage: 'linear-gradient(to right, hsl(200deg 73% 95%), hsl(200deg 70% 98%))',
    borderRadius: '5px',
    margin: '15px',
    minHeight: '62px'
})

const InfoFlex = styled(Flex)({
    flex: 3
})

const IconMessageFlex = styled(Flex)({
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex'
})

const ActionFlex = styled(Flex)({
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
})

export const HeaderField = div({
    display: 'flex',
    flexDirection: 'row',
    padding: '8px',
    fontSize: '14px',
    color: '#7cc8f3',
    fontWeight: 600
})

const FooterField = span({
    fontFamily: 'Montserrat',
    display: 'flex',
    flexDirection: 'row',
    padding: '8px',
    fontSize: '12px',
    lineHeight: '15px',
    color: '#a4b6c6',
    opacity: 0.8
});

const PickerContainer = styled(Flex)({
    padding: '8px',
})

const PickerLabel = div({
    alignItems: 'center',
    display: 'flex',
    float: 'left',
    fontSize: "9pt",
    color: '#a4b6c6'
});

const customFacilityStyles = {
    float: "left",
    backgroundColor: 'white',
    width: '308px',
    marginLeft: '17px'
};

const customTargetProgramStyles = {
    float: "left",
    backgroundColor: 'white',
    width: '308px',
    marginLeft: '10px'
};
